import { ApiError } from '../client.ts';

/**
 * Friendly copy for the patient-portal error vocabulary.
 *
 * Three closed unions reach this one surface — `PatientPortalErrorCode` (identity/session,
 * `patientAuth/routes.ts`), `SchedulingErrorCode` (`patientScheduling/routes.ts` reuses the staff
 * scheduling vocabulary wholesale), and `MessagingErrorCode` (`patientMessaging/routes.ts`) — and a
 * caller on this surface has no way to know in advance which of the three produced a given
 * `error.code`, so this is one switch over the union of all three rather than three parallel
 * functions. Deliberately **not** `web/src/api/client.ts`'s own `messageForCode`: that function is
 * the staff surface's vocabulary (`docs/decisions/patient-portal-frontend-scope.md` — a distinct
 * session/cookie needs a distinct error vocabulary, the same reasoning that keeps this whole
 * directory separate from `api/client.ts`'s staff-facing domain modules).
 */
export function patientPortalErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : 'Something went wrong.';
  }
  switch (error.code) {
    case 'invalid_request':
      return 'That request was not in a shape the server accepts.';
    case 'invalid_credentials':
      return 'That email and password combination was not recognized.';
    case 'mfa_required':
      return 'Enter the 6-digit code from your authenticator app, or a recovery code.';
    case 'invalid_totp_code':
      return 'That code was not recognized. Try again.';
    case 'mfa_already_enabled':
      return 'Two-factor authentication is already turned on for this account.';
    case 'invalid_or_expired_registration':
      return "That code wasn't recognized. Check your messages for the most recent one, or contact the practice if you're having trouble.";
    case 'unauthenticated':
      return 'Your session has ended. Sign in again to continue.';
    case 'audit_unavailable':
      return 'The change could not be recorded and was not made. Try again in a moment.';
    case 'rate_limited':
      return error.retryAfterSeconds === undefined
        ? 'Too many attempts. Wait a bit before trying again.'
        : `Too many attempts. Try again in ${String(error.retryAfterSeconds)} second${error.retryAfterSeconds === 1 ? '' : 's'}.`;
    // Scheduling (`SchedulingErrorCode`)
    case 'patient_not_found':
      return 'Your patient record could not be found.';
    case 'provider_not_found':
      return 'That provider is no longer available.';
    case 'not_found':
      return 'That record no longer exists.';
    case 'conflict':
      return 'That time is no longer available. Choose another.';
    case 'provider_unavailable':
      return 'The provider is not available at that time.';
    case 'invalid_status_transition':
      return 'That change is not allowed.';
    // Messaging (`MessagingErrorCode`)
    case 'thread_access_denied':
      return 'You do not have access to that conversation.';
    case 'no_care_team_relationship':
      return 'You do not have an assigned care team to message yet. Send a general question instead.';
    case 'thread_already_claimed':
      return 'That conversation has already been claimed by staff.';
    case 'thread_not_claimable':
      return 'That conversation cannot be claimed.';
    default:
      return error.message;
  }
}
